import { useContext, useEffect, useMemo } from "react";
import { isEmpty } from "@swiggy-private/js-utils";
import type { GetSwiggyHeadersResponsePayload } from "@swiggy-private/native-bridge";
import { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { QrCodeActions, ResponseContext } from "@swiggy-private/portal-dineout-context";
import { useFetchTrackData } from "./useFetchTrackData";
import { usePolling } from "./usePolling";
import { TRACK_POLLING_INTERVAL } from "../constants/track";

interface UseTrackPollingProps {
    apiHost: string;
    headers: GetSwiggyHeadersResponsePayload;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getCardDataByType: (cards: Array<any>, type: string) => Array<any> | null;
    forceFetch?: string;
}

interface UseTrackPollingResult {
    showEmptyOrderError: boolean;
    allCards: IPLWidgetControllerCards[];
}

/**
 * Main hook that composes useFetchTrackData and usePolling
 * to provide track order polling functionality
 */
export const useTrackPolling = ({
    apiHost,
    headers,
    getCardDataByType,
    forceFetch,
}: UseTrackPollingProps): UseTrackPollingResult => {
    const { state, dispatch } = useContext(ResponseContext);

    const { fetchTrackData, showEmptyOrderError, tableId, restaurantId } = useFetchTrackData({
        apiHost,
        headers,
        getCardDataByType,
    });

    // Determine if polling should be enabled
    const shouldFetch =
        !isEmpty(tableId) &&
        !isEmpty(restaurantId) &&
        (["bottomnav", "cart"].includes(forceFetch || "") ||
            isEmpty(state.track?.data?.success?.cards));

    // Calculate initial delay based on last update time
    const timeSinceLastUpdate = Date.now() - state.track.lastUpdated;
    const initialDelay = timeSinceLastUpdate > TRACK_POLLING_INTERVAL ? 0 : TRACK_POLLING_INTERVAL;

    // Start polling with the generic usePolling hook
    usePolling(fetchTrackData, {
        enabled: shouldFetch,
        interval: TRACK_POLLING_INTERVAL,
        initialDelay,
    });

    // Cleanup: persist state on unmount
    useEffect(() => {
        return () => {
            dispatch({
                type: QrCodeActions.TRACK_SCREEN_RESET,
                payload: {},
            });
        };
    }, [dispatch]);

    // Get cards from state - PLWidgetRenderer will handle popup cards internally
    const allCards = useMemo(() => {
        return state.track?.data?.success?.cards || [];
    }, [state.track?.data?.success?.cards]);

    return { showEmptyOrderError, allCards };
};
